import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { clearAllLiveThreads } from '../components/assistant/assistantLiveThread';
import { identifyUser, resetUser } from '../lib/posthog';
import { track, resetMeaningfulActionLatch } from '../lib/track';
import { authApi } from './api';
import { isAdmin, isInternal, isInvestor } from './roles';
import { createSessionRefresh } from './sessionRefresh';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [pending2fa, setPending2fa] = useState(false);
  const [needs2faSetup, setNeeds2faSetup] = useState(false);
  const [mustReset, setMustReset] = useState(false);
  const [needsEula, setNeedsEula] = useState(false);

  const applySession = useCallback((payload) => {
    if (!payload) {
      setUser(null);
      setPending2fa(false);
      setNeeds2faSetup(false);
      setMustReset(false);
      setNeedsEula(false);
      return;
    }
    const u = payload.user || payload;
    const pending = Boolean(payload.pending_2fa ?? u.pending_2fa);
    const setup = Boolean(payload.needs_2fa_setup ?? u.needs_2fa_setup);
    const reset = Boolean(payload.must_reset_password ?? u.must_reset_password);
    const eula = Boolean(payload.needs_eula ?? u.needs_eula);
    setUser(u);
    setPending2fa(pending);
    setNeeds2faSetup(setup);
    setMustReset(reset);
    // EULA only after login/2FA/password gates are clear.
    setNeedsEula(eula && !pending && !setup && !reset);
    if (u?.id != null) identifyUser(u);
  }, []);

  const applySessionRef = useRef(applySession);
  applySessionRef.current = applySession;

  const refreshCtrlRef = useRef(null);
  if (!refreshCtrlRef.current) {
    refreshCtrlRef.current = createSessionRefresh({
      fetchMe: () => authApi.me(),
      applySession: (payload) => applySessionRef.current(payload),
    });
  }

  const refresh = useCallback(() => refreshCtrlRef.current.refresh(), []);

  useEffect(() => {
    refresh().finally(() => setBootstrapping(false));
  }, [refresh]);

  const login = async (email, password) => {
    const res = await authApi.login(email, password);
    applySession(res);
    const u = res.user || res;
    if (u?.id != null) identifyUser(u);
    track('user_logged_in', {
      role: u?.role,
      pending_2fa: Boolean(res.pending_2fa),
      needs_2fa_setup: Boolean(res.needs_2fa_setup),
      login_method: 'password',
    });
    return res;
  };

  const logout = async () => {
    try {
      track('user_logged_out');
      await authApi.logout();
    } finally {
      // Drop the in-tab transcript before the next person can sign in here.
      clearAllLiveThreads();
      // Invalidate before clear so a late /auth/me cannot restore the user.
      refreshCtrlRef.current.invalidate();
      applySession(null);
      resetMeaningfulActionLatch();
      resetUser();
    }
  };

  const verify2fa = async (code) => {
    const res = await authApi.verify2fa(code);
    applySession({ ...res, pending_2fa: false });
    await refresh();
    track('user_2fa_verified', { role: res.user?.role || user?.role });
    return res;
  };

  const acceptEula = async () => {
    const res = await authApi.acceptEula();
    applySession({ ...res, needs_eula: false });
    await refresh();
    return res;
  };

  const value = {
    user,
    bootstrapping,
    pending2fa,
    needs2faSetup,
    mustReset,
    needsEula,
    isAdmin: isAdmin(user),
    isInvestor: isInvestor(user),
    isInternal: isInternal(user),
    isAuthed: Boolean(user) && !pending2fa && !needs2faSetup && !mustReset && !needsEula,
    login,
    logout,
    verify2fa,
    acceptEula,
    refresh,
    applySession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
